import { IconName } from '@fortawesome/fontawesome-svg-core';
import { ContextType } from '@bn/shared/entities/models';

export enum EventLogDimension {
  Feature = 'feature',
  Action = 'action',
  RuleId = 'ruleId',
  Ip = 'ip',
  Country = 'country',
  Asn = 'asn',
  Ja4 = 'ja4',
  UserAgent = 'ua',
  Url = 'url',
}

export enum EventLogAction {
  Blocked = 'Blocked',
  Challenged = 'Challenged',
  Logged = 'Logged',
}

export interface EventLogOption {
  id: string;
  label: string;
}

export const EVENT_LOG_FEATURES: EventLogOption[] = [
  { id: 'WAF', label: 'WAF' },
  { id: 'BTD', label: 'Bot detection' },
  { id: 'RTL', label: 'Rate limiting' },
  { id: 'ACL', label: 'Access lists' },
  { id: 'AGD', label: 'API Guardian' },
  { id: 'UPL', label: 'Upload Scanning' },
];

export const EVENT_LOG_ACTIONS: EventLogOption[] = [
  { id: EventLogAction.Blocked, label: 'Blocked' },
  { id: EventLogAction.Challenged, label: 'Challenged' },
  { id: EventLogAction.Logged, label: 'Logged' },
];

export type EventLogInputType = 'enum' | 'text';

export interface EventLogDimensionMeta {
  dimension: EventLogDimension;
  label: string;
  shortLabel: string;
  icon: IconName;
  filterable: boolean;
  groupable: boolean;
  inputType: EventLogInputType;
  options?: EventLogOption[];
  placeholder?: string;
  searchable?: boolean;
}

export const EVENT_LOG_DIMENSIONS: EventLogDimensionMeta[] = [
  {
    dimension: EventLogDimension.Feature,
    label: 'Feature',
    shortLabel: 'Feature',
    icon: 'shield-halved',
    filterable: true,
    groupable: true,
    inputType: 'enum',
    options: EVENT_LOG_FEATURES,
  },
  {
    dimension: EventLogDimension.Action,
    label: 'Action',
    shortLabel: 'Action',
    icon: 'bolt',
    filterable: true,
    groupable: true,
    inputType: 'enum',
    options: EVENT_LOG_ACTIONS,
  },
  {
    dimension: EventLogDimension.RuleId,
    label: 'Rule ID',
    shortLabel: 'Rule',
    icon: 'tag',
    filterable: true,
    groupable: true,
    inputType: 'text',
    placeholder: 'e.g. 953101',
  },
  {
    dimension: EventLogDimension.Ip,
    label: 'IP / range',
    shortLabel: 'IP',
    icon: 'location-dot',
    filterable: true,
    groupable: true,
    inputType: 'text',
    placeholder: 'IP address, or CIDR',
  },
  {
    dimension: EventLogDimension.Country,
    label: 'Country',
    shortLabel: 'Country',
    icon: 'flag',
    filterable: true,
    groupable: true,
    inputType: 'enum',
    searchable: true,
    options: [],
  },
  {
    dimension: EventLogDimension.Asn,
    label: 'ASN',
    shortLabel: 'ASN',
    icon: 'chart-network',
    filterable: true,
    groupable: true,
    inputType: 'text',
    placeholder: 'e.g. 396982',
  },
  {
    dimension: EventLogDimension.Ja4,
    label: 'JA4 fingerprint',
    shortLabel: 'JA4',
    icon: 'key',
    filterable: true,
    groupable: true,
    inputType: 'text',
    placeholder: 'JA4 fingerprint',
  },
  {
    dimension: EventLogDimension.UserAgent,
    label: 'User-Agent',
    shortLabel: 'UA',
    icon: 'browser',
    filterable: true,
    groupable: true,
    inputType: 'text',
    placeholder: 'User-Agent',
  },
  {
    dimension: EventLogDimension.Url,
    label: 'URL',
    shortLabel: 'URL',
    icon: 'link',
    filterable: true,
    groupable: true,
    inputType: 'text',
    placeholder: 'URL path',
  },
];

export const EVENT_LOG_DIMENSION_MAP: Record<string, EventLogDimensionMeta> = Object.fromEntries(EVENT_LOG_DIMENSIONS.map((meta) => [meta.dimension, meta]));

export interface EventLogAppliedFilter {
  field: EventLogDimension;
  op: string;
  value: string[];
}

export interface EventLogHeader {
  Name?: string;
  Value?: string;
}

export interface EventLogTriggerMatch {
  Key?: string;
  Value?: string;
}

export interface EventRowLogContext {
  Headers?: EventLogHeader[];
  TriggerMatch?: EventLogTriggerMatch;
  Url?: string;
  UrlEncodedBody?: string | null;
  Message?: string;
  RemoteIp?: string;
  [key: string]: unknown;
}

const TEXT_MATCH_FIELDS = [EventLogDimension.Url, EventLogDimension.Ja4, EventLogDimension.UserAgent, EventLogDimension.RuleId];

export function buildFilterOp(field: EventLogDimension, value: string): string {
  if (field === EventLogDimension.Ip) return 'cidr';

  if (TEXT_MATCH_FIELDS.includes(field)) return value.includes('*') ? 'wildcard' : 'contains';

  return 'eq';
}

export function eventLogActionColor(action?: string): 'red' | 'premium' | 'blue' | 'grey' {
  switch (action) {
    case EventLogAction.Blocked:
      return 'red';
    case EventLogAction.Challenged:
      return 'premium';
    case EventLogAction.Logged:
      return 'blue';
    default:
      return 'grey';
  }
}

export function eventLogMethodColor(method?: string | null): ContextType {
  switch (method?.toUpperCase()) {
    case 'GET':
      return 'info';
    case 'POST':
    case 'PATCH':
      return 'success';
    case 'PUT':
      return 'warning';
    case 'DELETE':
      return 'danger';
    default:
      return 'light';
  }
}

export function eventLogValueLabel(field: string, value: string): string {
  return EVENT_LOG_DIMENSION_MAP[field]?.options?.find((option) => option.id === value)?.label ?? value;
}

export function eventLogFeatureIcon(feature?: string): IconName {
  switch (feature) {
    case 'WAF':
      return 'shield-halved';
    case 'BTD':
      return 'robot';
    case 'RTL':
      return 'gauge';
    case 'ACL':
      return 'list';
    case 'AGD':
      return 'code';
    case 'UPL':
      return 'file-shield';
    default:
      return 'shield-halved';
  }
}
